import { useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@shared/ui/styled';

const Root = styled('div')(() => ({
  position: 'relative',
  padding: '8px 4px',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  transition: 'background 1.5s ease',
  '&:not(:last-child)': {
    borderBottom: '1px solid var(--qhs-border)',
  },
  '&[data-flash="true"]': {
    background: 'rgba(255, 200, 0, 0.18)',
    transition: 'background 0s',
  },
}));

const InlineRow = styled('div')({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 12,
});

const Label = styled('span')({
  fontFamily: 'var(--qhs-font-body)',
  fontSize: 12.5,
  fontWeight: 500,
  color: 'var(--qhs-text-2)',
  lineHeight: 1.25,
  flexShrink: 1,
  minWidth: 0,
});

const Value = styled('span')({
  fontFamily: 'var(--qhs-font-body)',
  fontVariantNumeric: 'tabular-nums lining-nums',
  fontSize: 15,
  fontWeight: 700,
  color: 'var(--qhs-text-1)',
  lineHeight: 1.1,
  letterSpacing: '-0.2px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
});

// Thin percentage bar shown below rate rows. Track = --qhs-border,
// fill = --qhs-brand. Clamped to [0, 100] because rare backend
// aggregations round to slightly over 1.0 (e.g. 100.05%).
const RateTrack = styled('div')({
  height: 4,
  borderRadius: 999,
  background: 'var(--qhs-border)',
  overflow: 'hidden',
});

const RateFill = styled('div')({
  height: '100%',
  background: 'var(--qhs-brand, #00A8FF)',
  borderRadius: 'inherit',
  transition: 'width 200ms ease',
});

// Currency formatter from the legacy table helper returns "$ 1,234.56"
// with a space after the dollar sign — trim it so the value stays
// compact on its own line.
const normalize = value => (typeof value === 'string' ? value.replace(/^\$\s+/, '$') : value);

/**
 * Single stat row inside the Metrics pane.
 *
 * Compact inline layout: label on the left, value on the right, one
 * row per metric — mirrors the legacy `InfoRow` density the customer
 * asked us back for (2026-08 review, Improvement #6). For rate-type
 * metrics an extra thin progress bar renders under the row so the
 * percentage reads at a glance without parsing digits.
 *
 * Flashes yellow when the value changes so a live filter update in
 * the background table surfaces in the pane. Skips the flash on
 * initial mount to keep first paint quiet.
 */
const MetricCard = ({ label, value, ratePercent }) => {
  const displayValue = normalize(value);
  const valueKey = useMemo(() => String(displayValue ?? ''), [displayValue]);

  const [flash, setFlash] = useState(false);
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return undefined;
    }
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 1500);
    return () => clearTimeout(t);
  }, [valueKey]);

  const clampedRate =
    typeof ratePercent === 'number' && Number.isFinite(ratePercent)
      ? Math.max(0, Math.min(100, ratePercent))
      : null;

  return (
    <Root data-flash={flash || undefined}>
      <InlineRow>
        <Label>{label}</Label>
        <Value>{displayValue}</Value>
      </InlineRow>
      {clampedRate != null && (
        <RateTrack>
          <RateFill style={{ width: `${clampedRate}%` }} />
        </RateTrack>
      )}
    </Root>
  );
};

export default MetricCard;
